import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  AlertTriangle, Check, Info, Loader2, Pickaxe, Plus, RefreshCw, Save, Shuffle, Trash2, Undo2,
} from 'lucide-react';
import { apiService, type ConfigInspection, type ConfigSectionsQuery, type ContainerApplyResult } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { ApplyReport, ContainerScope, NoReference } from '@/components/config/AllContainers';
import parityVector from './minerais/parity-vector.json';
import { WORLD_CEILING_Y, WORLD_FLOOR_Y, canonicalPlacements, parseJavaOreConfig } from './minerais/oreGenerator';
import {
  ALL_FACES, buildLevels, formatLocal, isObject, levelProblems, listWorlds, newAutoRegen, newClusterType, newLayer, newLine,
  newOres, parseAnchor, readAutoRegen, readOres, schedule, toGenConfig,
  type Json, type JsonObject, type Problem, type WorldEntry,
} from './minerais/oreSettings';
import { blockColor, meanClusters, rockColor, simulate, type Simulation } from './minerais/oreSimulation';
import base from './LootboxPage.module.scss';
import weapon from './WeaponsPage.module.scss';
import styles from './OreGenerationPage.module.scss';

// ============================================================
// Génération de minerais : clés Ores et AutoRegen des mondes de worldorigin.config.json (WorldOriginMod)
// ============================================================
// Un bloc de Levels par monde (désigné par ses Servers : earth, edora, moon, farmland...). La page ne
// touche que deux clés de chaque bloc : Ores (couches, lignes de minerai, amas des grottes) et AutoRegen
// (régénération à date fixe). Le reste du fichier n'est ni affiché ni modifié.
//
// Même mécanisme que les autres configs de mod (Armes) : la requête nomme WorldOriginMod, le serveur ne
// retient que les containers dont l'image le porte, et n'écrit que Ores et AutoRegen (WorldOriginOres.java).
// Tant que l'API en ligne ne connaît pas WorldOriginMod (« Mod inconnu »), la page lit et écrit le fichier
// par son nom, comme les pages Lootbox ou Factions : les contrôles sont alors ceux de la page seule.
// Un enregistrement écrit dans le dossier de travail de l'image ; il arrive sur les serveurs à la
// publication (Envoyer), pas avant : WorldOriginMod ne relit sa config qu'au démarrage.
//
// L'aperçu (coupe, rendement) est calculé par le générateur du mod, rejoué ici à l'identique
// (minerais/oreGenerator.ts, repris de l'aperçu de NG Java) et vérifié à l'ouverture par le vecteur de
// parité commun à NG Java, NGWorldEdit et WorldOriginMod.
// ============================================================

const FILE = 'worldorigin.config.json';
const MOD = 'WorldOriginMod';
const MOD_QUERY: ConfigSectionsQuery = { fileNames: [FILE], keys: ['Levels'], mod: MOD };
const FILE_QUERY: ConfigSectionsQuery = { fileNames: [FILE], keys: ['Levels'] };

type Tab = 'ores' | 'clusters' | 'auto' | 'preview';

/** Ce que l'API sait de WorldOriginMod dans chaque image (WorldOriginOres.java) : version qui lit Ores et AutoRegen. */
interface ModCatalog {
  minVersion: string;
  images: Array<{ containerId: string; containerName: string; version: string | null }>;
}

/** Compare deux versions x.y.z (négatif : a avant b). */
function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map((n) => Number.parseInt(n, 10) || 0);
  const pb = b.split('.').map((n) => Number.parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

const DIST_LABEL: Record<string, string> = { uniform: 'Uniforme', triangle: 'Triangle', trapezoid: 'Trapèze' };
const SHAPE_LABEL: Record<string, string> = { blob: 'Filon', scatter: 'Dispersé' };
const FACE_LABEL: Record<string, string> = {
  up: 'Sol (pousse vers le haut)', down: 'Plafond (pousse vers le bas)', north: 'Mur (vers le nord)',
  south: 'Mur (vers le sud)', west: "Mur (vers l'ouest)", east: "Mur (vers l'est)",
};
const DAY_OPTIONS = [
  ['Monday', 'lundi'], ['Tuesday', 'mardi'], ['Wednesday', 'mercredi'], ['Thursday', 'jeudi'],
  ['Friday', 'vendredi'], ['Saturday', 'samedi'], ['Sunday', 'dimanche'],
] as const;

/** Blocs proposés à la saisie, en plus de ceux du fichier. */
const SUGGESTED_BLOCKS = [
  'minecraft:stone', 'minecraft:deepslate', 'minecraft:coal_ore', 'minecraft:iron_ore', 'minecraft:copper_ore',
  'minecraft:gold_ore', 'minecraft:redstone_ore', 'minecraft:lapis_ore', 'minecraft:diamond_ore', 'minecraft:emerald_ore',
  'ng:tin_ore', 'ng:aluminium_ore', 'ng:silicon_ore', 'ng:uranium_ore', 'propsi:raw_marble',
];

const fmt = (n: number, digits = 1) => n.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: digits });

function withField(o: JsonObject, key: string, value: Json | undefined): JsonObject {
  const out: JsonObject = { ...o };
  if (value === undefined) delete out[key];
  else out[key] = value;
  return out;
}

function listText(v: Json | undefined): string {
  return Array.isArray(v) ? v.filter((s) => typeof s === 'string').join(', ') : '';
}

function textList(text: string): string[] {
  return text.split(/[,\n]/).map((s) => s.trim()).filter((s) => s.length > 0);
}

// ==================== Champs ====================

/** Nombre saisi en texte : le brouillon reste affiché tant que la saisie est en cours ; vide = null si `optional`. */
function NumInput({ value, onChange, optional, className, ariaLabel, placeholder, disabled }: {
  value: number | null | undefined;
  onChange: (v: number | null) => void;
  optional?: boolean;
  className?: string;
  ariaLabel?: string;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? (value === null || value === undefined ? '' : String(value).replace('.', ','));
  return (
    <input className={className} type="text" inputMode="decimal" value={shown} placeholder={placeholder}
      aria-label={ariaLabel} disabled={disabled}
      onChange={(e) => {
        const text = e.target.value;
        setDraft(text);
        const t = text.trim().replace(',', '.');
        if (t === '') { if (optional) onChange(null); return; }
        const n = Number(t);
        if (Number.isFinite(n)) onChange(n);
      }}
      onBlur={() => setDraft(null)} />
  );
}

/** Liste d'identifiants séparés par des virgules ; la valeur n'est appliquée qu'à la sortie du champ. */
function ListInput({ value, onChange, className, ariaLabel, placeholder }: {
  value: Json | undefined;
  onChange: (v: string[]) => void;
  className?: string;
  ariaLabel?: string;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <input className={className} type="text" value={draft ?? listText(value)} placeholder={placeholder} aria-label={ariaLabel}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => { if (draft !== null) onChange(textList(draft)); setDraft(null); }} />
  );
}

function Field({ label, unit, help, problem, wide, children }: {
  label: string;
  unit?: string;
  help?: ReactNode;
  problem?: Problem;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <label className={`${weapon.field} ${wide ? weapon.fieldWide : ''}`}>
      <span className={weapon.fieldLabel}>{label}{unit && <em>{unit}</em>}</span>
      <span className={`${weapon.inputUnit} ${problem ? weapon.inputBad : ''}`}>{children}</span>
      {problem && <span className={weapon.fieldError}>{problem.message}</span>}
      {help && <span className={weapon.fieldHelp}>{help}</span>}
    </label>
  );
}

function CheckField({ label, checked, onChange, help }: { label: string; checked: boolean; onChange: (v: boolean) => void; help?: ReactNode }) {
  return (
    <div className={weapon.field}>
      <label className={weapon.check}>
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} /> {label}
      </label>
      {help && <span className={weapon.fieldHelp}>{help}</span>}
    </div>
  );
}

// ==================== Parité du générateur ====================

/** Rejoue le vecteur de parité (le même que NG Java, NGWorldEdit et WorldOriginMod) : l'aperçu est-il fidèle ? */
async function checkParity(): Promise<{ ok: boolean; text: string }> {
  const vector = parityVector as unknown as { config: Record<string, unknown>; chunks: number[][]; expected: { digest: string; total: number } };
  const config = parseJavaOreConfig(vector.config, 0n);
  let payload = '';
  let total = 0;
  for (const [cx, cz] of vector.chunks) {
    const placements = canonicalPlacements(config, cx, cz);
    payload += `${cx};${cz}|`;
    for (const p of placements) payload += `${p}|`;
    payload += '\n';
    total += placements.length;
  }
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(payload));
  const digest = Array.from(new Uint8Array(bytes)).map((b) => b.toString(16).padStart(2, '0')).join('');
  const ok = digest === vector.expected.digest && total === vector.expected.total;
  return {
    ok,
    text: ok
      ? `Aperçu fidèle au mod : vecteur de parité vérifié (${total} blocs, condensé ${digest.slice(0, 16)})`
      : `Aperçu NON fidèle : le vecteur de parité donne ${total} blocs et ${digest.slice(0, 16)} au lieu de ${vector.expected.total} et ${vector.expected.digest.slice(0, 16)}`,
  };
}

// ==================== Page ====================

export function OreGenerationPage() {
  const activeGameId = useGameStore((s) => s.activeGameId);
  const [inspection, setInspection] = useState<ConfigInspection | null>(null);
  const [catalog, setCatalog] = useState<ModCatalog | null>(null);
  const [query, setQuery] = useState<ConfigSectionsQuery>(MOD_QUERY);
  /** Levels tels que lus dans le fichier de référence. */
  const [loaded, setLoaded] = useState<JsonObject[] | null>(null);
  /** Clés Ores et AutoRegen modifiées, par rang de monde (undefined : clé retirée). */
  const [edits, setEdits] = useState<Record<number, { Ores?: Json; AutoRegen?: Json }>>({});
  const [selected, setSelected] = useState(0);
  const [tab, setTab] = useState<Tab>('ores');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ ok: boolean; msg: string } | null>(null);
  const [report, setReport] = useState<ContainerApplyResult | null>(null);
  const [parity, setParity] = useState<{ ok: boolean; text: string } | null>(null);
  const [draw, setDraw] = useState(0);
  const [fullHeight, setFullHeight] = useState(false);
  const [sim, setSim] = useState<{ key: string; result: Simulation } | null>(null);
  const [computing, setComputing] = useState(false);
  const [hover, setHover] = useState<string>('');
  const loadSeq = useRef(0);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    let active = true;
    // Après le premier rendu : le vecteur ne bloque pas l'ouverture de la page.
    const t = setTimeout(() => { void checkParity().then((r) => { if (active) setParity(r); }); }, 50);
    return () => { active = false; clearTimeout(t); };
  }, []);

  /**
   * Lit Levels dans le container de référence. Si l'API ne connaît pas encore WorldOriginMod, relit le
   * fichier par son nom. `keepEdit` ne rafraîchit que le bandeau, après un enregistrement.
   */
  const load = useCallback(async (keepEdit = false) => {
    const seq = ++loadSeq.current;
    setLoading(true);
    if (!keepEdit) {
      setStatus(null);
      setReport(null);
      setLoaded(null);
    }
    let q = MOD_QUERY;
    let r = await apiService.inspectContainerConfig(q);
    if (r.data && !r.data.success && /mod inconnu/i.test(r.data.message ?? '')) {
      q = FILE_QUERY;
      r = await apiService.inspectContainerConfig(q);
    }
    if (seq !== loadSeq.current) return;
    setLoading(false);
    setQuery(q);
    setInspection(r.data ?? null);
    const table = r.data?.catalog as ModCatalog | null | undefined;
    setCatalog(table && typeof table.minVersion === 'string' && Array.isArray(table.images) ? table : null);
    if (keepEdit) return;
    if (!r.data) { setStatus({ ok: false, msg: r.error ?? 'Lecture impossible.' }); return; }
    if (!r.data.referenceContainerId) return;
    const raw = (r.data.sections ?? {}) as Record<string, unknown>;
    const file = r.data.referencePath ?? FILE;
    if (!Array.isArray(raw.Levels) || raw.Levels.some((l) => !isObject(l))) {
      setStatus({ ok: false, msg: `${file} : Levels n'a pas la forme attendue (liste de mondes). Corrige le fichier dans Paramètres généraux → Containers.` });
      return;
    }
    const levels = raw.Levels as JsonObject[];
    setLoaded(levels);
    setEdits({});
    const worlds = listWorlds(levels);
    const first = worlds.find((w) => w.hasOres || w.hasAuto) ?? worlds[0];
    setSelected(first ? first.index : 0);
  }, []);

  useEffect(() => { void load(); }, [activeGameId, load]);

  const levels = useMemo(() => (loaded ? buildLevels(loaded, edits) : []), [loaded, edits]);
  const worlds = useMemo(() => listWorlds(levels), [levels]);
  const world: WorldEntry | null = worlds[selected] ?? null;
  const current = levels[selected] ?? null;
  const oresRaw = current && isObject(current.Ores) ? current.Ores : null;
  const autoRaw = current && isObject(current.AutoRegen) ? current.AutoRegen : null;
  const ores = useMemo(() => readOres(oresRaw), [oresRaw]);
  const auto = useMemo(() => readAutoRegen(autoRaw), [autoRaw]);
  const problemsByWorld = useMemo(() => levels.map(levelProblems), [levels]);
  const problems = problemsByWorld[selected] ?? [];
  const problemAt = (path: string) => problems.find((p) => p.path === path);
  /** Graines que le navigateur ne sait pas relire sans les arrondir (au-delà de 2^53). */
  const unsafeSeeds = useMemo(() => worlds.filter((w) => {
    const seed = isObject(levels[w.index].Ores) ? (levels[w.index].Ores as JsonObject).Seed : undefined;
    return typeof seed === 'number' && !Number.isSafeInteger(seed);
  }), [worlds, levels]);
  const dirty = !!loaded && JSON.stringify(levels) !== JSON.stringify(loaded);
  const blockedWorlds = worlds.filter((w) => (problemsByWorld[w.index]?.length ?? 0) > 0);
  const blocked = blockedWorlds.length > 0 || unsafeSeeds.length > 0;
  const editedWorlds = worlds.filter((w) => loaded && JSON.stringify(levels[w.index]) !== JSON.stringify(loaded[w.index]));

  /** Blocs du fichier, proposés à la saisie. */
  const knownBlocks = useMemo(() => {
    const out = new Set<string>(SUGGESTED_BLOCKS);
    for (const level of levels) {
      const o = readOres(level.Ores);
      o.layers.forEach((l) => { out.add(l.fillBlock); l.ores.forEach((line) => line.block && out.add(line.block)); });
      o.clusters.types.forEach((t) => t.block && out.add(t.block));
      o.hostBlocks.forEach((b) => out.add(b));
    }
    return [...out].filter((b) => b.length > 0).sort();
  }, [levels]);

  // ==================== Aperçu (calcul différé) ====================

  const previewSeed = ores.seed !== 0n ? ores.seed + BigInt(draw) : BigInt(draw);
  const genConfig = useMemo(() => toGenConfig(ores, previewSeed), [ores, previewSeed]);
  const simKey = useMemo(() => `${selected}|${previewSeed}|${JSON.stringify(oresRaw?.Layers ?? null)}`, [selected, previewSeed, oresRaw]);

  // Calcul dans un worker (des filons énormes demandent des secondes) ; une nouvelle valeur arrête le calcul en cours.
  const workerRef = useRef<Worker | null>(null);
  useEffect(() => () => workerRef.current?.terminate(), []);
  useEffect(() => {
    if (!oresRaw) return undefined;
    setComputing(true);
    const key = simKey;
    const t = setTimeout(() => {
      workerRef.current?.terminate();
      workerRef.current = null;
      const done = (result: Simulation) => { setSim({ key, result }); setComputing(false); };
      let worker: Worker;
      try {
        worker = new Worker(new URL('./minerais/oreWorker.ts', import.meta.url), { type: 'module' });
      } catch {
        done(simulate(genConfig)); // Sans worker (navigateur ancien) : calcul sur place.
        return;
      }
      workerRef.current = worker;
      worker.onmessage = (event: MessageEvent<Simulation>) => {
        if (workerRef.current === worker) workerRef.current = null;
        worker.terminate();
        done(event.data);
      };
      worker.onerror = () => {
        if (workerRef.current === worker) workerRef.current = null;
        worker.terminate();
        setComputing(false);
      };
      worker.postMessage({ ores: oresRaw, seed: previewSeed.toString() });
    }, 300);
    return () => clearTimeout(t);
  }, [simKey, genConfig, oresRaw, previewSeed]);

  const result = sim && oresRaw ? sim.result : null;
  const fresh = !!sim && sim.key === simKey;
  /** Rendement d'une ligne (couche, rang) dans le dernier calcul. */
  const yieldOf = (li: number, oi: number) => result?.yields.find((y) => y.ore.layerIndex === li && y.ore.oreIndex === oi) ?? null;

  /** Altitudes montrées par la coupe : la bande des minerais, ou toute la hauteur. */
  const yRange = useMemo(() => {
    if (fullHeight || genConfig.activeOres.length === 0) return { top: WORLD_CEILING_Y, bottom: WORLD_FLOOR_Y };
    const bottom = Math.min(...genConfig.activeOres.map((o) => o.minY));
    const top = Math.min(Math.max(...genConfig.activeOres.map((o) => o.maxY)), bottom + 191);
    return { top, bottom };
  }, [fullHeight, genConfig]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !result || tab !== 'preview') return;
    const rows = yRange.top - yRange.bottom + 1;
    canvas.width = result.sliceWidth;
    canvas.height = rows;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const image = ctx.createImageData(result.sliceWidth, rows);
    const px = image.data;
    const probe = document.createElement('canvas').getContext('2d', { willReadFrequently: true });
    const hex = (c: string) => {
      if (!probe) return [128, 128, 128];
      probe.fillStyle = c;
      probe.fillRect(0, 0, 1, 1);
      const d = probe.getImageData(0, 0, 1, 1).data;
      return [d[0], d[1], d[2]];
    };
    const oreRgb = genConfig.activeOres.map((o) => hex(blockColor(o.block)));
    const rockRgb = new Map<string, number[]>();
    const rockAt = (y: number) => {
      const layer = genConfig.layers.find((l) => y >= l.minY && y <= l.maxY);
      const key = layer ? layer.fill : '';
      if (!rockRgb.has(key)) rockRgb.set(key, layer ? hex(rockColor(layer.fill)) : [24, 24, 28]);
      return rockRgb.get(key)!;
    };
    for (let row = 0; row < rows; row += 1) {
      const y = yRange.top - row;
      const rock = rockAt(y);
      const sliceRow = WORLD_CEILING_Y - y;
      for (let x = 0; x < result.sliceWidth; x += 1) {
        const i = result.slice[sliceRow * result.sliceWidth + x];
        const o = (row * result.sliceWidth + x) * 4;
        const c = i >= 0 ? oreRgb[i] : rock.map((v) => v * 0.62);
        px[o] = c[0]; px[o + 1] = c[1]; px[o + 2] = c[2]; px[o + 3] = 255;
      }
    }
    ctx.putImageData(image, 0, 0);
  }, [result, tab, yRange, genConfig]);

  // ==================== Modifications ====================

  /** Remplace la clé Ores ou AutoRegen du monde affiché (undefined : la retire du monde). */
  const updateLevel = (key: 'Ores' | 'AutoRegen', fn: (o: JsonObject) => JsonObject | undefined) => {
    if (!loaded) return;
    setEdits((prev) => {
      const edit = prev[selected] ?? {};
      const currentValue = key in edit ? edit[key] : loaded[selected]?.[key];
      const baseValue = isObject(currentValue) ? currentValue : key === 'Ores' ? newOres() : newAutoRegen();
      return { ...prev, [selected]: { ...edit, [key]: fn(baseValue) } };
    });
  };
  const setOres = (key: string, value: Json | undefined) => updateLevel('Ores', (o) => withField(o, key, value));
  const setAuto = (key: string, value: Json | undefined) => updateLevel('AutoRegen', (o) => withField(o, key, value));
  const layersOf = (o: JsonObject): JsonObject[] => (Array.isArray(o.Layers) ? o.Layers.filter(isObject) : []);
  const setLayer = (li: number, fn: (l: JsonObject) => JsonObject | null) => updateLevel('Ores', (o) => {
    const layers = layersOf(o).map((l, i) => (i === li ? fn(l) : l)).filter((l): l is JsonObject => l !== null);
    return { ...o, Layers: layers };
  });
  const setLine = (li: number, oi: number, key: string, value: Json | undefined) => setLayer(li, (l) => {
    const lines = (Array.isArray(l.Ores) ? l.Ores : []).map((line, i) => (i === oi && isObject(line) ? withField(line, key, value) : line));
    return { ...l, Ores: lines };
  });
  const removeLine = (li: number, oi: number) => setLayer(li, (l) => ({ ...l, Ores: (Array.isArray(l.Ores) ? l.Ores : []).filter((_, i) => i !== oi) }));
  const clustersOf = (o: JsonObject): JsonObject => (isObject(o.Clusters) ? o.Clusters : (newOres().Clusters as JsonObject));
  const setClusters = (key: string, value: Json | undefined) => updateLevel('Ores', (o) => ({ ...o, Clusters: withField(clustersOf(o), key, value) }));
  const setClusterType = (ti: number, key: string, value: Json | undefined) => updateLevel('Ores', (o) => {
    const c = clustersOf(o);
    const types = (Array.isArray(c.Types) ? c.Types : []).map((t, i) => (i === ti && isObject(t) ? withField(t, key, value) : t));
    return { ...o, Clusters: { ...c, Types: types } };
  });

  /** Oublie les modifications du monde affiché. */
  const revertWorld = () => setEdits((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => Number(k) !== selected)));

  // ==================== Sauvegarde ====================

  const save = async () => {
    if (!loaded || !dirty || blocked) return;
    setSaving(true);
    setStatus(null);
    setReport(null);
    const sent = levels;
    const res = await apiService.applyContainerConfig({ ...query, sections: { Levels: sent } });
    setSaving(false);
    if (!res.data) {
      setStatus({ ok: false, msg: res.error ?? 'Enregistrement en échec' });
      return;
    }
    setReport(res.data);
    if (res.data.success) {
      setLoaded(sent);
      setEdits({});
    }
    void load(true);
  };

  const reload = () => {
    if (dirty && !window.confirm(`Recharger ${FILE} abandonnera les modifications non enregistrées. Continuer ?`)) return;
    void load();
  };

  const missing = inspection && !inspection.referenceContainerId ? inspection : null;
  const ready = !!loaded && !!world;
  const fileMode = query === FILE_QUERY;

  // ==================== Rendu : réglages et couches ====================

  const cellClass = (path: string) => `${styles.cell} ${problemAt(path) ? styles.cellBad : ''}`;

  const renderOres = () => {
    if (!oresRaw) {
      return (
        <section className={styles.card}>
          <p className={base.muted}>
            Ce monde n'a pas de génération de minerais : ses chunks recopiés depuis l'origine (régénération, /regenmap,
            rollback) gardent les minerais de l'origine tels quels.
          </p>
          <div>
            <button type="button" className={styles.smallBtn} onClick={() => updateLevel('Ores', () => newOres())}>
              <Plus size={14} /> Ajouter une génération de minerais (éteinte)
            </button>
          </div>
        </section>
      );
    }
    const raw = oresRaw;
    const layers = layersOf(raw);
    return (
      <>
        <section className={styles.card}>
          <div className={styles.cardHead}>
            <h3>Réglages</h3>
            <div className={styles.cardActions}>
              <button type="button" className={styles.smallBtn}
                onClick={() => {
                  if (window.confirm(`Retirer la génération de minerais de ${world?.label} ? Ses chunks recopiés depuis l'origine garderont les minerais de l'origine.`)) {
                    updateLevel('Ores', () => undefined);
                  }
                }}>
                <Trash2 size={14} /> Retirer
              </button>
            </div>
          </div>
          <div className={weapon.grid}>
            <CheckField label="Génération allumée" checked={ores.enabled} onChange={(v) => setOres('Enabled', v)}
              help="Chaque chunk recopié depuis l'origine (régénération automatique, /regenworld, /regenmap, rollback) perd les minerais de l'origine et reçoit les filons ci-dessous." />
            <Field label="Graine" problem={problemAt('Ores.Seed')}
              help="0 : la graine du monde. Changer la graine redistribue tous les filons.">
              <NumInput value={typeof raw.Seed === 'number' ? raw.Seed : null} placeholder={typeof raw.Seed === 'string' ? raw.Seed : '0'}
                onChange={(v) => setOres('Seed', Math.trunc(v ?? 0))} ariaLabel="Graine" />
            </Field>
            <CheckField label="Nouveau tirage à chaque régénération" checked={ores.newDrawEachRegen} onChange={(v) => setOres('NewDrawEachRegen', v)}
              help="Chaque régénération automatique place les filons ailleurs (mêmes densités). Décoché : mêmes positions tant que la graine et les lignes ne changent pas." />
            <CheckField label="Variantes ardoise" checked={ores.deepslateVariants} onChange={(v) => setOres('DeepslateVariants', v)}
              help="Dans l'ardoise des abîmes, un minerai du jeu de base prend sa variante d'ardoise (deepslate_coal_ore...)." />
            <Field label="Roches remplaçables" wide problem={problems.find((p) => p.path.startsWith('Ores.HostBlocks'))}
              help="Un filon ne remplace que ces blocs (séparés par des virgules). Vide : tout sauf l'air.">
              <ListInput value={raw.HostBlocks} onChange={(v) => setOres('HostBlocks', v)} ariaLabel="Roches remplaçables" />
            </Field>
            <Field label="Blocs effacés avant la génération" wide problem={problems.find((p) => p.path.startsWith('Ores.PurgeBlocks'))}
              help="Minerais et amas déjà dans l'origine, effacés avant de poser les nouveaux (motif finissant par * : ng:ore_cluster_*). Un amas devient de l'air, un minerai la roche voisine.">
              <ListInput value={raw.PurgeBlocks} onChange={(v) => setOres('PurgeBlocks', v)} ariaLabel="Blocs effacés" />
            </Field>
            <Field label="Roche de rebouchage hors couche" problem={problemAt('Ores.PurgeFillBlock')}
              help="Roche qui remplace un minerai effacé à une altitude qu'aucune couche ne couvre.">
              <input list="ore-blocks" value={typeof raw.PurgeFillBlock === 'string' ? raw.PurgeFillBlock : ''}
                onChange={(e) => setOres('PurgeFillBlock', e.target.value.trim())} aria-label="Roche de rebouchage" />
            </Field>
          </div>
        </section>

        <p className={base.muted}>
          Chaque ligne tire ses filons d'après son rang (couche, puis ligne) : retirer ou insérer une ligne déplace les filons des
          lignes suivantes ; décocher « Active » coupe une ligne sans rien déplacer. Les lignes reprennent celles de la page
          « Génération de minerais » du panel NG Java (tentatives par chunk, tailles, loi d'altitude, forme).
        </p>

        {layers.map((layer, li) => {
          const view = ores.layers[li];
          const lines = Array.isArray(layer.Ores) ? layer.Ores : [];
          const lp = `Ores.Layers[${li}]`;
          return (
            <section key={li} className={styles.card}>
              <div className={styles.cardHead}>
                <h3>Couche {li + 1} : {view?.name || 'sans nom'}</h3>
                <div className={styles.cardActions}>
                  <button type="button" className={styles.smallBtn}
                    onClick={() => setLayer(li, (l) => ({ ...l, Ores: [...(Array.isArray(l.Ores) ? l.Ores : []), newLine()] }))}>
                    <Plus size={14} /> Ligne
                  </button>
                  <button type="button" className={styles.smallBtn}
                    onClick={() => {
                      if (window.confirm(`Retirer la couche « ${view?.name || li + 1} » et ses ${lines.length} ligne(s) ? Les filons des couches suivantes changent de place.`)) {
                        setLayer(li, () => null);
                      }
                    }}>
                    <Trash2 size={14} /> Couche
                  </button>
                </div>
              </div>
              <div className={weapon.grid}>
                <Field label="Nom" problem={problemAt(`${lp}.Name`)}>
                  <input value={typeof layer.Name === 'string' ? layer.Name : ''} onChange={(e) => setLayer(li, (l) => withField(l, 'Name', e.target.value))} aria-label="Nom de la couche" />
                </Field>
                <Field label="Bas" unit="Y" problem={problemAt(`${lp}.MinY`)}>
                  <NumInput value={typeof layer.MinY === 'number' ? layer.MinY : WORLD_FLOOR_Y} onChange={(v) => setLayer(li, (l) => withField(l, 'MinY', v ?? WORLD_FLOOR_Y))} ariaLabel="Bas de la couche" />
                </Field>
                <Field label="Haut" unit="Y" problem={problemAt(`${lp}.MaxY`)}>
                  <NumInput value={typeof layer.MaxY === 'number' ? layer.MaxY : WORLD_CEILING_Y} onChange={(v) => setLayer(li, (l) => withField(l, 'MaxY', v ?? WORLD_CEILING_Y))} ariaLabel="Haut de la couche" />
                </Field>
                <Field label="Roche de la couche" problem={problemAt(`${lp}.FillBlock`)} help="Rebouche un minerai effacé à cette altitude.">
                  <input list="ore-blocks" value={typeof layer.FillBlock === 'string' ? layer.FillBlock : ''} onChange={(e) => setLayer(li, (l) => withField(l, 'FillBlock', e.target.value.trim()))} aria-label="Roche de la couche" />
                </Field>
              </div>
              <div className={styles.lines}>
                <div className={styles.lineHead}>
                  <span title="Active">Act.</span><span>Nom</span><span>Bloc</span><span title="Tentatives par chunk (fraction : probabilité)">Tent./chunk</span>
                  <span>Taille min</span><span>Taille max</span><span>Loi</span><span>Plateau</span><span>Y min</span><span>Y max</span>
                  <span>Forme</span><span title="Rayon d'éparpillement (dispersé)">Dispers.</span><span title="Blocs réellement posés par chunk, en roche pleine">Posés/chunk</span><span />
                </div>
                {lines.map((line, oi) => {
                  if (!isObject(line)) return null;
                  const p = `${lp}.Ores[${oi}]`;
                  const v = view?.ores[oi];
                  const y = yieldOf(li, oi);
                  return (
                    <div key={oi} className={`${styles.line} ${v && !v.active ? styles.lineOff : ''}`}>
                      <input type="checkbox" checked={v?.active ?? true} onChange={(e) => setLine(li, oi, 'Active', e.target.checked)} aria-label="Ligne active" />
                      <input className={cellClass(`${p}.Name`)} value={typeof line.Name === 'string' ? line.Name : ''} onChange={(e) => setLine(li, oi, 'Name', e.target.value)} aria-label="Nom" />
                      <input className={cellClass(`${p}.Block`)} list="ore-blocks" value={typeof line.Block === 'string' ? line.Block : ''}
                        onChange={(e) => setLine(li, oi, 'Block', e.target.value.trim())} aria-label="Bloc" title={problemAt(`${p}.Block`)?.message ?? (v?.javaId ? `Java : ${v.javaId}` : '')} />
                      <NumInput className={cellClass(`${p}.Attempts`)} value={v?.attempts ?? 0} onChange={(n) => setLine(li, oi, 'Attempts', n ?? 0)} ariaLabel="Tentatives par chunk" />
                      <NumInput className={cellClass(`${p}.SizeMin`)} value={v?.sizeMin ?? 8} onChange={(n) => setLine(li, oi, 'SizeMin', n ?? 8)} ariaLabel="Taille minimale" />
                      <NumInput className={cellClass(`${p}.SizeMax`)} optional value={v?.sizeMax ?? null} placeholder={String(v?.sizeMin ?? '')}
                        onChange={(n) => setLine(li, oi, 'SizeMax', n === null ? undefined : n)} ariaLabel="Taille maximale" />
                      <select className={cellClass(`${p}.Dist`)} value={v?.dist ?? 'uniform'} onChange={(e) => setLine(li, oi, 'Dist', e.target.value)} aria-label="Loi d'altitude">
                        {Object.entries(DIST_LABEL).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                      </select>
                      <NumInput className={cellClass(`${p}.Plateau`)} value={v?.plateau ?? 0} disabled={v?.dist !== 'trapezoid'}
                        onChange={(n) => setLine(li, oi, 'Plateau', n === null || n === 0 ? undefined : n)} ariaLabel="Plateau" />
                      <NumInput className={cellClass(`${p}.MinY`)} optional value={v?.minY ?? null} placeholder={String(view?.minY ?? '')}
                        onChange={(n) => setLine(li, oi, 'MinY', n === null ? undefined : n)} ariaLabel="Y minimal" />
                      <NumInput className={cellClass(`${p}.MaxY`)} optional value={v?.maxY ?? null} placeholder={String(view?.maxY ?? '')}
                        onChange={(n) => setLine(li, oi, 'MaxY', n === null ? undefined : n)} ariaLabel="Y maximal" />
                      <select className={cellClass(`${p}.Shape`)} value={v?.shape ?? 'blob'} onChange={(e) => setLine(li, oi, 'Shape', e.target.value)} aria-label="Forme">
                        {Object.entries(SHAPE_LABEL).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                      </select>
                      <NumInput className={cellClass(`${p}.ScatterSpread`)} value={v?.scatterSpread ?? 6} disabled={v?.shape !== 'scatter'}
                        onChange={(n) => setLine(li, oi, 'ScatterSpread', n ?? 6)} ariaLabel="Rayon d'éparpillement" />
                      <span className={styles.yield}>
                        {y ? <><span className={styles.swatch} style={{ background: blockColor(y.ore.block) }} /> {fmt(y.placed)}</> : '–'}
                      </span>
                      <button type="button" className={styles.iconBtn} title="Retirer la ligne (les filons des lignes suivantes changent de place)"
                        onClick={() => {
                          if (window.confirm(`Retirer la ligne « ${v?.name || oi + 1} » ? Les filons des lignes suivantes de la couche changent de place ; pour la couper sans rien déplacer, décoche « Active ».`)) {
                            removeLine(li, oi);
                          }
                        }}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
              {problems.filter((p) => p.path.startsWith(`${lp}.Ores[`)).map((p) => (
                <span key={p.path} className={weapon.fieldError}>{lineLabel(p.path, ores)} : {p.message}</span>
              ))}
            </section>
          );
        })}
        <div>
          <button type="button" className={styles.smallBtn} onClick={() => updateLevel('Ores', (o) => ({ ...o, Layers: [...layersOf(o), newLayer()] }))}>
            <Plus size={14} /> Ajouter une couche
          </button>
        </div>
      </>
    );
  };

  // ==================== Rendu : amas ====================

  const renderClusters = () => {
    if (!oresRaw) {
      return <p className={base.muted}>Les amas font partie de la génération de minerais : ajoute-la d'abord (onglet Minerais).</p>;
    }
    const c = clustersOf(oresRaw);
    const view = ores.clusters;
    const types = Array.isArray(c.Types) ? c.Types : [];
    const totalWeight = view.types.reduce((a, t) => a + Math.max(0, t.weight), 0);
    const perChunk = view.perChunk;
    const sum = perChunk.reduce((a, b) => a + b, 0);
    return (
      <>
        <section className={styles.card}>
          <div className={weapon.grid}>
            <CheckField label="Amas allumés" checked={view.enabled} onChange={(v) => setClusters('Enabled', v)}
              help="Amas de minerai (ng:ore_cluster_*) posés sur les faces des grottes, tournés vers le vide comme un amas posé à la main." />
            <Field label="Sous la surface" unit="blocs" problem={problemAt('Ores.Clusters.Cover')}
              help="Un amas est au moins à cette distance sous le dessus de sa colonne : pas de cristaux en surface.">
              <NumInput value={view.cover} onChange={(v) => setClusters('Cover', v ?? 6)} ariaLabel="Distance sous la surface" />
            </Field>
            <Field label="Supports" wide problem={problems.find((p) => p.path.startsWith('Ores.Clusters.Supports'))}
              help="Blocs qui portent un amas (la paroi), séparés par des virgules. Vide : les roches remplaçables, la roche naturelle et les minerais.">
              <ListInput value={c.Supports} onChange={(v) => setClusters('Supports', v)} ariaLabel="Supports" />
            </Field>
          </div>
          <h3 className={weapon.groupTitle}>Nombre d'amas dans un chunk qui a une grotte</h3>
          <div className={weapon.grid}>
            {perChunk.map((p, k) => (
              <Field key={k} label={`${k} amas`} unit="%" problem={k === 0 ? problemAt('Ores.Clusters.PerChunk') : undefined}>
                <NumInput value={Math.round(p * 10000) / 100} onChange={(v) => {
                  const next = [...perChunk];
                  next[k] = Math.max(0, (v ?? 0) / 100);
                  setClusters('PerChunk', next);
                }} ariaLabel={`Probabilité de ${k} amas`} />
              </Field>
            ))}
          </div>
          <div className={styles.cardActions}>
            <button type="button" className={styles.smallBtn} onClick={() => setClusters('PerChunk', [...perChunk, 0])}><Plus size={14} /> Un amas de plus</button>
            <button type="button" className={styles.smallBtn} disabled={perChunk.length <= 1} onClick={() => setClusters('PerChunk', perChunk.slice(0, -1))}><Trash2 size={14} /> Retirer le dernier</button>
            <span className={base.muted}>
              Somme {fmt(sum * 100)} % ({sum < 0.999 ? `le reste, ${fmt((1 - sum) * 100)} %, compte comme 0 amas` : 'complet'}) ;
              en moyenne {fmt(meanClusters(perChunk), 2)} amas par chunk qui a une grotte.
            </span>
          </div>
          <h3 className={weapon.groupTitle}>Faces des grottes</h3>
          <div className={weapon.chips}>
            {ALL_FACES.map((f) => (
              <label key={f} className={weapon.check}>
                <input type="checkbox" checked={view.faces.includes(f)}
                  onChange={(e) => setClusters('Faces', e.target.checked ? [...view.faces, f] : view.faces.filter((x) => x !== f))} /> {FACE_LABEL[f]}
              </label>
            ))}
          </div>
        </section>
        <section className={styles.card}>
          <div className={styles.cardHead}>
            <h3>Sortes d'amas</h3>
            <button type="button" className={styles.smallBtn} onClick={() => updateLevel('Ores', (o) => {
              const cl = clustersOf(o);
              return { ...o, Clusters: { ...cl, Types: [...(Array.isArray(cl.Types) ? cl.Types : []), newClusterType()] } };
            })}><Plus size={14} /> Sorte</button>
          </div>
          <p className={weapon.groupNote}>
            À une altitude donnée, la sorte est tirée au poids parmi celles dont la bande couvre cette altitude.
          </p>
          <div className={styles.yieldTable}>
            <div className={styles.yieldHead}>
              <span>Bloc</span><span>Bande <em>(Y)</em></span><span>Poids</span><span>Part du poids total</span><span />
            </div>
            {types.map((t, ti) => {
              if (!isObject(t)) return null;
              const v = view.types[ti];
              const tp = `Ores.Clusters.Types[${ti}]`;
              return (
                <div key={ti} className={styles.yieldRow}>
                  <span className={styles.legendItem}>
                    <span className={styles.swatch} style={{ background: blockColor(v?.block ?? '') }} />
                    <input className={cellClass(`${tp}.Block`)} list="ore-blocks" value={typeof t.Block === 'string' ? t.Block : ''}
                      onChange={(e) => setClusterType(ti, 'Block', e.target.value.trim())} aria-label="Bloc de l'amas" />
                  </span>
                  <span className={styles.legendItem}>
                    <NumInput className={cellClass(`${tp}.MinY`)} value={v?.minY ?? WORLD_FLOOR_Y} onChange={(n) => setClusterType(ti, 'MinY', n ?? WORLD_FLOOR_Y)} ariaLabel="Bas de la bande" />
                    à
                    <NumInput className={cellClass(`${tp}.MaxY`)} value={v?.maxY ?? WORLD_CEILING_Y} onChange={(n) => setClusterType(ti, 'MaxY', n ?? WORLD_CEILING_Y)} ariaLabel="Haut de la bande" />
                  </span>
                  <NumInput className={cellClass(`${tp}.Weight`)} value={v?.weight ?? 1} onChange={(n) => setClusterType(ti, 'Weight', n ?? 1)} ariaLabel="Poids" />
                  <span className={styles.yield}>{totalWeight > 0 && v ? `${fmt((Math.max(0, v.weight) / totalWeight) * 100)} %` : '–'}</span>
                  <button type="button" className={styles.iconBtn} title="Retirer cette sorte" onClick={() => updateLevel('Ores', (o) => {
                    const cl = clustersOf(o);
                    return { ...o, Clusters: { ...cl, Types: (Array.isArray(cl.Types) ? cl.Types : []).filter((_, i) => i !== ti) } };
                  })}><Trash2 size={14} /></button>
                </div>
              );
            })}
          </div>
          {problems.filter((p) => p.path.startsWith('Ores.Clusters.Types')).map((p) => (
            <span key={p.path} className={weapon.fieldError}>{p.path.replace('Ores.Clusters.Types', 'Sorte ').replace(/\[(\d+)\]/, (_, n) => `${Number(n) + 1}`)} : {p.message}</span>
          ))}
        </section>
      </>
    );
  };

  // ==================== Rendu : régénération automatique ====================

  const renderAuto = () => {
    if (!autoRaw) {
      return (
        <section className={styles.card}>
          <p className={base.muted}>Ce monde n'a pas de régénération automatique.</p>
          <div>
            <button type="button" className={styles.smallBtn} onClick={() => updateLevel('AutoRegen', () => newAutoRegen())}>
              <Plus size={14} /> Ajouter une régénération automatique (éteinte)
            </button>
          </div>
        </section>
      );
    }
    const tz = world?.timeZone ?? 'Europe/Paris';
    const plan = schedule(auto, tz, Date.now());
    const fixed = !plan.auto && auto.anchor.trim().toLowerCase() !== 'auto';
    const anchorValue = fixed ? (parseAnchor(auto.anchor) ? auto.anchor.slice(0, 16).replace(' ', 'T') : auto.anchor) : '';
    return (
      <>
        <section className={styles.card}>
          <div className={styles.cardHead}>
            <h3>Régénération automatique</h3>
            <button type="button" className={styles.smallBtn}
              onClick={() => { if (window.confirm(`Retirer la régénération automatique de ${world?.label} ?`)) updateLevel('AutoRegen', () => undefined); }}>
              <Trash2 size={14} /> Retirer
            </button>
          </div>
          {auto.enabled && (
            <div className={base.warnBox}>
              <AlertTriangle size={14} />
              <span>
                Allumée : à chaque date, tout {world?.label} redevient celui de l'origine (constructions comprises). Les joueurs présents
                partent {auto.sendPlayersTo ? `vers « ${auto.sendPlayersTo} »` : 'déconnectés'}, le monde reste fermé pendant la passe puis rouvre.
              </span>
            </div>
          )}
          <div className={weapon.grid}>
            <CheckField label="Régénération allumée" checked={auto.enabled} onChange={(v) => setAuto('Enabled', v)} />
            <Field label="Intervalle" problem={problemAt('AutoRegen.Every')} help="14d : tous les 14 jours (d, h, m, s).">
              <input value={auto.every} onChange={(e) => setAuto('Every', e.target.value.trim())} aria-label="Intervalle" />
            </Field>
            <div className={weapon.field}>
              <span className={weapon.fieldLabel}>Première date</span>
              <label className={weapon.check}>
                <input type="radio" checked={!fixed} onChange={() => setAuto('Anchor', 'auto')} /> Au premier démarrage avec la régénération
              </label>
              <label className={weapon.check}>
                <input type="radio" checked={fixed} onChange={() => setAuto('Anchor', anchorValue || nextMondayInput())} /> Date fixe
              </label>
            </div>
            {fixed ? (
              <Field label="Date fixe" unit={tz} problem={problemAt('AutoRegen.Anchor')}
                help="Heure locale du monde. Les dates suivantes en découlent, tous les intervalles.">
                <input type="datetime-local" value={anchorValue} onChange={(e) => setAuto('Anchor', e.target.value.slice(0, 16))} aria-label="Date fixe" />
              </Field>
            ) : (
              <>
                <Field label="Jour" problem={problemAt('AutoRegen.DayOfWeek')}>
                  <select value={DAY_OPTIONS.find(([en, fr]) => [en.toLowerCase(), fr].includes(auto.dayOfWeek.trim().toLowerCase()))?.[0] ?? auto.dayOfWeek}
                    onChange={(e) => setAuto('DayOfWeek', e.target.value)} aria-label="Jour">
                    {DAY_OPTIONS.map(([en, fr]) => <option key={en} value={en}>{fr}</option>)}
                  </select>
                </Field>
                <Field label="Heure" unit={tz} problem={problemAt('AutoRegen.Time')}>
                  <input type="time" value={auto.time} onChange={(e) => setAuto('Time', e.target.value)} aria-label="Heure" />
                </Field>
                <Field label="Délai minimal" unit="heures" problem={problemAt('AutoRegen.MinLeadHours')}
                  help="Entre le premier démarrage et la première régénération : le temps de la première annonce.">
                  <NumInput value={auto.minLeadHours} onChange={(v) => setAuto('MinLeadHours', v ?? 24)} ariaLabel="Délai minimal" />
                </Field>
              </>
            )}
            <Field label="Annonces avant" wide problem={problems.find((p) => p.path.startsWith('AutoRegen.AnnounceBefore'))}
              help="Dans le tchat du monde, ces durées avant chaque régénération (séparées par des virgules : 24h, 1h, 10m, 1m).">
              <ListInput value={autoRaw.AnnounceBefore} onChange={(v) => setAuto('AnnounceBefore', v)} ariaLabel="Annonces avant" />
            </Field>
            <Field label="Destination des joueurs" problem={problemAt('AutoRegen.SendPlayersTo')}
              help="capital : la capitale du groupe du joueur (résolue par le proxy). Vide : déconnectés avec le message.">
              <input value={auto.sendPlayersTo} onChange={(e) => setAuto('SendPlayersTo', e.target.value.trim())} aria-label="Destination" />
            </Field>
            <Field label="Nom dans les messages" problem={problemAt('AutoRegen.DisplayName')} help="Vide : le nom du monde.">
              <input value={auto.displayName} onChange={(e) => setAuto('DisplayName', e.target.value)} aria-label="Nom dans les messages" />
            </Field>
            <Field label="Message d'accueil" unit="minutes" problem={problemAt('AutoRegen.WelcomeMinutes')}
              help="Après la réouverture, chaque arrivant lit que le monde vient d'être régénéré pendant cette durée.">
              <NumInput value={auto.welcomeMinutes} onChange={(v) => setAuto('WelcomeMinutes', v ?? 60)} ariaLabel="Message d'accueil" />
            </Field>
            <Field label="Permission pour rester" problem={problemAt('AutoRegen.BypassPermission')} help="Laisse entrer pendant la fermeture. Vide : personne.">
              <input value={auto.bypassPermission} onChange={(e) => setAuto('BypassPermission', e.target.value.trim())} aria-label="Permission" />
            </Field>
            <Field label="Chunks à la fois" problem={problemAt('AutoRegen.Threads')}>
              <NumInput value={auto.threads} onChange={(v) => setAuto('Threads', v ?? 4)} ariaLabel="Chunks à la fois" />
            </Field>
            <Field label="Débit maximal" unit="chunks/s" problem={problemAt('AutoRegen.ChunksPerSecond')} help="0 : sans limite (le monde est fermé et vide).">
              <NumInput value={auto.chunksPerSecond} onChange={(v) => setAuto('ChunksPerSecond', v ?? 0)} ariaLabel="Débit maximal" />
            </Field>
            <Field label="TPS minimal" problem={problemAt('AutoRegen.MinTps')} help="Pause tant que le TPS du monde est en dessous. 0 : jamais.">
              <NumInput value={auto.minTps} onChange={(v) => setAuto('MinTps', v ?? 0)} ariaLabel="TPS minimal" />
            </Field>
            <CheckField label="Épargner les pavés copiés en place" checked={auto.spareActiveRegions} onChange={(v) => setAuto('SpareActiveRegions', v)}
              help="Les villages de la farmland posés depuis un autre monde, pas encore remis à neuf, ne sont pas régénérés." />
            <CheckField label="Terminer le village en place au départ" checked={auto.endVillagesBefore} onChange={(v) => setAuto('EndVillagesBefore', v)}
              help="Comme /village terminer maintenant (NGBestiaryMod) : ses habitants ne reviennent pas sur la carte neuve." />
          </div>
        </section>
        <section className={styles.card}>
          <h3 className={weapon.groupTitle}>Prochaines régénérations{plan.auto ? ' (si le serveur démarrait maintenant)' : ''}</h3>
          {plan.problem ? (
            <span className={weapon.fieldError}>Calendrier illisible : {plan.problem}.</span>
          ) : (
            <ol className={styles.dates}>
              {plan.next.map((t) => <li key={t}>{formatLocal(t, tz)} ({tz})</li>)}
            </ol>
          )}
          <p className={weapon.groupNote}>
            {plan.auto
              ? "Avec la première date « au premier démarrage », chaque serveur la calcule à son premier démarrage avec la régénération allumée puis la garde : deux serveurs promus ensemble tombent le même jour, sauf démarrage de part et d'autre de l'heure limite. Une date fixe l'évite."
              : 'Un serveur rallumé moins de 6 h après une date (ou de la moitié de l\'intervalle s\'il est plus court) lance quand même la régénération ; au-delà, elle attend la date suivante.'}
            {' '}Rien ne part avant la publication de l'image : le mod ne relit sa config qu'au démarrage.
          </p>
        </section>
      </>
    );
  };

  // ==================== Rendu : aperçu ====================

  const renderPreview = () => {
    if (!oresRaw) return <p className={base.muted}>Ce monde n'a pas de génération de minerais : rien à montrer.</p>;
    if (genConfig.activeOres.length === 0) return <p className={base.muted}>Aucune ligne active avec un bloc et des tentatives : rien n'est posé.</p>;
    const rows = yRange.top - yRange.bottom + 1;
    return (
      <>
        <section className={styles.card}>
          <div className={styles.previewHead}>
            <h3 className={weapon.groupTitle}>
              Coupe verticale : {result ? result.sliceWidth : 256} blocs de large, Y {yRange.bottom} à {yRange.top}
              {computing || !fresh ? <> <Loader2 size={12} className={base.spin} /></> : null}
            </h3>
            <div className={styles.cardActions}>
              <button type="button" className={styles.smallBtn} onClick={() => setDraw((d) => d + 1)}><Shuffle size={14} /> Autre tirage</button>
              <label className={weapon.check}><input type="checkbox" checked={fullHeight} onChange={(e) => setFullHeight(e.target.checked)} /> Toute la hauteur</label>
            </div>
          </div>
          <div className={styles.canvasWrap}>
            {yTicks(genConfig.layers, yRange).map((y) => (
              <span key={y} className={styles.yTick} style={{ top: `${((yRange.top - y) / rows) * 100}%` }}>Y {y}</span>
            ))}
            <canvas ref={canvasRef} className={styles.canvas} style={{ aspectRatio: `${result?.sliceWidth ?? 256} / ${rows}` }}
              onMouseMove={(e) => {
                if (!result) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const x = Math.floor(((e.clientX - rect.left) / rect.width) * result.sliceWidth);
                const yy = yRange.top - Math.floor(((e.clientY - rect.top) / rect.height) * rows);
                const i = x >= 0 && x < result.sliceWidth && yy >= WORLD_FLOOR_Y && yy <= WORLD_CEILING_Y ? result.slice[(WORLD_CEILING_Y - yy) * result.sliceWidth + x] : -1;
                const ore = i >= 0 ? genConfig.activeOres[i] : null;
                setHover(`x ${x}, y ${yy}${ore ? ` : ${ore.name} (${ore.block})` : ''}`);
              }}
              onMouseLeave={() => setHover('')} />
          </div>
          <div className={styles.axis}><span>x 0</span><span className={styles.hover}>{hover}</span><span>x {(result?.sliceWidth ?? 256) - 1}</span></div>
          <div className={styles.legend}>
            {genConfig.activeOres.map((o) => (
              <span key={`${o.layerIndex}-${o.oreIndex}`} className={styles.legendItem}>
                <span className={styles.swatch} style={{ background: blockColor(o.block) }} /> {ores.layers[o.layerIndex]?.name} · {o.name}
              </span>
            ))}
          </div>
          <p className={weapon.groupNote}>
            Une tranche d'un bloc d'épaisseur, en roche pleine, telle que le mod la poserait : c'est ce qu'on voit en creusant un tunnel.
            {ores.seed === 0n
              ? ' Graine 0 : celle du monde, que le panel ne connaît pas ; la coupe montre un tirage d\'exemple (mêmes densités, mêmes formes).'
              : ''}
            {ores.newDrawEachRegen ? ' Avec « nouveau tirage à chaque régénération », les filons changent de place à chaque fois.' : ''}
          </p>
        </section>
        <section className={styles.card}>
          <h3 className={weapon.groupTitle}>Rendement par chunk{result ? ` (moyenne sur ${result.chunks} chunks)` : ''}</h3>
          {result?.partial && (
            <span className={weapon.fieldWarn}>
              Échantillon écourté : ces filons sont si gros que le calcul s'est arrêté à {result.chunks} chunks (les nombres restent des moyennes).
            </span>
          )}
          <div className={styles.yieldTable}>
            <div className={styles.yieldHead}>
              <span>Ligne</span><span>Bloc</span><span>Bande <em>(Y)</em></span><span>Posés <em>(roche pleine)</em></span><span>Appels <em>(page Java)</em></span>
            </div>
            {result?.yields.map((y) => (
              <div key={`${y.ore.layerIndex}-${y.ore.oreIndex}`} className={styles.yieldRow}>
                <span>{ores.layers[y.ore.layerIndex]?.name} · {y.ore.name}</span>
                <span className={styles.legendItem}><span className={styles.swatch} style={{ background: blockColor(y.ore.block) }} /> <code>{y.ore.block}</code></span>
                <span className={styles.yield}>{y.ore.minY} à {y.ore.maxY}</span>
                <span className={styles.yield}>{fmt(y.placed)}</span>
                <span className={styles.yield}>{fmt(y.calls)}</span>
              </div>
            ))}
            {result && (
              <div className={`${styles.yieldRow} ${styles.yieldTotal}`}>
                <span>Total</span><span /><span />
                <span className={styles.yield}>{fmt(result.totalPlaced)}</span>
                <span className={styles.yield}>{fmt(result.totalCalls)}</span>
              </div>
            )}
          </div>
          <p className={weapon.groupNote}>
            « Posés » : les blocs que le mod pose vraiment dans une roche pleine (un bloc déjà devenu minerai n'est plus remplaçable).
            « Appels » : ce que la page Java appelle « Rendement par minerai », chaque appel du générateur, doublons compris : c'est ce
            nombre qui donnait 4 854 blocs par chunk sur la farmland. Sur la vraie carte, c'est moins (grottes, eau, surface, rien
            au-dessus du relief) : la farmland du 24/10 a reçu 272 blocs de minerai par chunk en moyenne, environ un cinquième de la
            roche pleine. Le mod écrit les vrais nombres dans son journal à la fin de chaque passe.
          </p>
        </section>
      </>
    );
  };

  // ==================== Rendu ====================

  return (
    <div className={base.page}>
      <header className={base.header}>
        <div>
          <h1 className={base.title}>Génération de minerais</h1>
          <p className={base.subtitle}>
            Filons, amas des grottes et régénération automatique des mondes de WorldOriginMod (clés Ores et AutoRegen de chaque monde
            de {FILE}). Commun à tous les containers du projet dont l'image porte WorldOriginMod. Un changement arrive sur les serveurs
            à la publication de l'image (Envoyer), pas avant. Le reste du fichier (origine, bornes, rollback) n'est ni affiché ni modifié.
          </p>
          {parity && (
            <span className={`${styles.parity} ${parity.ok ? '' : styles.parityBad}`}>
              {parity.ok ? <Check size={12} /> : <AlertTriangle size={12} />} {parity.text}
            </span>
          )}
        </div>
        <div className={base.headerActions}>
          <button className={base.secondaryBtn} onClick={reload} disabled={loading}>
            {loading ? <Loader2 size={14} className={base.spin} /> : <RefreshCw size={14} />} Rafraîchir
          </button>
          <button className={base.primaryBtn} onClick={save} disabled={!dirty || saving || blocked}
            title={blocked ? "Corrige d'abord les valeurs signalées : le mod les refuserait" : `Écrit les mondes modifiés dans ${FILE}`}>
            {saving ? <Loader2 size={14} className={base.spin} /> : <Save size={14} />} Enregistrer
          </button>
        </div>
      </header>

      {inspection && (
        <ContainerScope entries={inspection.entries} referencePath={inspection.referencePath} labelOf={() => 'mondes (Ores, AutoRegen)'} />
      )}

      {fileMode && inspection?.referenceContainerId && (
        <div className={weapon.infoBox}>
          <Info size={14} />
          <span>
            L'API du panel en ligne ne connaît pas encore WorldOriginMod : la page lit et écrit {FILE} par son nom, et les contrôles
            sont ceux de la page. Au prochain déploiement de l'API, le serveur vérifiera aussi les valeurs et n'écrira que les clés
            Ores et AutoRegen de chaque monde.
          </span>
        </div>
      )}

      {catalog && catalog.images
        .filter((img) => img.version === null || compareVersions(img.version, catalog.minVersion) < 0)
        .map((img) => (
          <div key={img.containerId} className={base.warnBox}>
            <AlertTriangle size={14} />
            <span>
              {img.version === null
                ? <>Version de WorldOriginMod illisible pour l'image de <strong>{img.containerName}</strong> (release-version.json).</>
                : <>L'image de <strong>{img.containerName}</strong> porte WorldOriginMod {img.version} : elle ignore Ores et AutoRegen
                  tant qu'elle n'est pas montée en {catalog.minVersion} ou plus (la montée se fait à la tournée de l'image).</>}
            </span>
          </div>
        ))}

      {status && (
        <div className={status.ok ? base.ok : base.error}>
          {status.ok ? <Check size={14} /> : <AlertTriangle size={14} />} {status.msg}
        </div>
      )}

      {report && <ApplyReport result={report} onClose={() => setReport(null)} />}

      {ready && unsafeSeeds.length > 0 && (
        <div className={base.error}>
          <AlertTriangle size={14} />
          <span>
            Graine trop grande pour le navigateur ({unsafeSeeds.map((w) => w.label).join(', ')}) : elle serait arrondie à l'enregistrement.
            Rien ne peut être enregistré d'ici ; change-la dans Paramètres généraux → Containers.
          </span>
        </div>
      )}

      {ready && dirty && blockedWorlds.length > 0 && (
        <div className={base.error}>
          <AlertTriangle size={14} />
          <span>
            À corriger avant d'enregistrer (le mod refuserait ces valeurs) :{' '}
            {blockedWorlds.map((w) => `${w.label} (${problemsByWorld[w.index].length} valeur(s))`).join(' ; ')}.
          </span>
        </div>
      )}

      {!ready ? (
        <section className={base.detail}>
          <div className={base.detailEmpty}>
            {loading ? (
              <><Loader2 size={22} className={base.spin} /><p>Ouverture de {FILE}…</p></>
            ) : missing && missing.entries.length === 0 ? (
              <>
                <AlertTriangle size={22} />
                <p>{missing.message}</p>
                <p className={base.muted}>Déclare les containers du projet dans Paramètres généraux → Containers.</p>
              </>
            ) : missing ? (
              <NoReference file={FILE} entries={missing.entries} mod={fileMode ? undefined : MOD} />
            ) : loaded && loaded.length === 0 ? (
              <><Pickaxe size={26} /><p>{FILE} ne déclare aucun monde (Levels vide).</p></>
            ) : (
              <><Pickaxe size={26} /><p>{FILE} n'a pas pu être ouvert.</p></>
            )}
          </div>
        </section>
      ) : (
        <>
          <div className={styles.worlds}>
            {worlds.map((w) => (
              <button key={w.index} type="button" className={`${styles.world} ${w.index === selected ? styles.worldOn : ''}`}
                aria-pressed={w.index === selected} onClick={() => setSelected(w.index)}>
                <strong>{w.label}</strong>
                <code>{w.servers.join(', ') || 'tous les serveurs'}</code>
                <span className={styles.worldBadges}>
                  <span className={`${weapon.badge} ${w.oresOn ? weapon.badgeAccent : ''}`}>{w.hasOres ? (w.oresOn ? 'minerais' : 'minerais éteints') : 'sans minerais'}</span>
                  <span className={`${weapon.badge} ${w.autoOn ? weapon.badgeAccent : ''}`}>{w.hasAuto ? (w.autoOn ? 'régénération auto' : 'régénération éteinte') : 'sans régénération'}</span>
                  {(problemsByWorld[w.index]?.length ?? 0) > 0 && <span className={`${weapon.badge} ${weapon.badgeBad}`}>à corriger</span>}
                  {editedWorlds.some((e) => e.index === w.index) && <span className={`${weapon.badge} ${weapon.badgeWarn}`}>modifié</span>}
                </span>
              </button>
            ))}
          </div>

          <nav className={base.tabBar}>
            {([['ores', 'Minerais'], ['clusters', 'Amas'], ['auto', 'Régénération automatique'], ['preview', 'Aperçu et rendement']] as const).map(([k, label]) => (
              <button key={k} type="button" className={`${base.tabBtn} ${tab === k ? base.tabBtnOn : ''}`} onClick={() => setTab(k)}>{label}</button>
            ))}
          </nav>

          {tab === 'ores' && renderOres()}
          {tab === 'clusters' && renderClusters()}
          {tab === 'auto' && renderAuto()}
          {tab === 'preview' && renderPreview()}

          <datalist id="ore-blocks">
            {knownBlocks.map((b) => <option key={b} value={b} />)}
          </datalist>

          <div className={base.footerActions}>
            <span>
              {editedWorlds.some((e) => e.index === selected) && (
                <button type="button" className={styles.smallBtn} onClick={revertWorld}><Undo2 size={14} /> Oublier les modifications de {world?.label}</button>
              )}
            </span>
            <span className={base.muted}>
              {dirty ? `Modifications non enregistrées (${editedWorlds.map((w) => w.label).join(', ')}).` : 'Enregistré.'}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

/** « Couche 2, ligne 8 (Alu) » pour un chemin Ores.Layers[1].Ores[7].Champ. */
function lineLabel(path: string, ores: ReturnType<typeof readOres>): string {
  const m = /Layers\[(\d+)\]\.Ores\[(\d+)\]/.exec(path);
  if (!m) return path;
  const li = Number(m[1]);
  const oi = Number(m[2]);
  const name = ores.layers[li]?.ores[oi]?.name;
  return `Ligne ${oi + 1}${name ? ` (${name})` : ''}`;
}

/** Repères d'altitude de la coupe : haut, bas, et bords des couches qui tombent dedans. */
function yTicks(layers: Array<{ minY: number; maxY: number }>, range: { top: number; bottom: number }): number[] {
  const out = new Set<number>([range.top, range.bottom]);
  for (const l of layers) {
    for (const y of [l.minY, l.maxY]) if (y > range.bottom + 8 && y < range.top - 8) out.add(y);
  }
  return [...out].sort((a, b) => b - a);
}

/** Le lundi qui suit, à 5 h, au format d'un champ datetime-local. */
function nextMondayInput(): string {
  const d = new Date();
  const add = ((8 - d.getDay()) % 7) || 7;
  d.setDate(d.getDate() + add);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T05:00`;
}

